// Capture is decided per request. Cloud sync requires a choice for the current
// account and backend on this device, independently from canvas-copy hints.
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { resolveApiUrl } from "@/api/client";
import {
  getDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
} from "./desktop-chat-auth-scope";
import { syncPersistedPreferences } from "./sync-persisted-preferences";

interface ScreenshotSettings {
  syncScreenshots: boolean;
  syncScreenshotsByAccount: Record<string, boolean>;
  setSyncScreenshots: (v: boolean) => void;
  copySentImagesToCanvas: boolean;
  setCopySentImagesToCanvas: (v: boolean) => void;
  voiceHintSeenAt: number | null;
  setVoiceHintSeenAt: (v: number | null) => void;
}

function accountKey(): string | null {
  const scope = getDesktopChatAuthScope();
  return scope
    ? `${new URL(resolveApiUrl("/"), window.location.href).origin}\n${scope.accountId}`
    : null;
}
function syncChoices(value: unknown): Record<string, boolean> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(([, chosen]) => typeof chosen === "boolean"),
  );
}
function currentChoice(choices: Record<string, boolean>): boolean {
  const key = accountKey();
  return key ? choices[key] === true : false;
}

export const useScreenshotSettings = create<ScreenshotSettings>()(
  persist(
    (set) => ({
      syncScreenshots: false,
      syncScreenshotsByAccount: {},
      setSyncScreenshots: (value) =>
        set((state) => {
          const key = accountKey();
          if (!key) return { syncScreenshots: false };
          return {
            syncScreenshots: value,
            syncScreenshotsByAccount: {
              ...state.syncScreenshotsByAccount,
              [key]: value,
            },
          };
        }),
      copySentImagesToCanvas: false,
      setCopySentImagesToCanvas: (v) => set({ copySentImagesToCanvas: v }),
      voiceHintSeenAt: null,
      setVoiceHintSeenAt: (v) => set({ voiceHintSeenAt: v }),
    }),
    {
      name: "denker-screenshot-settings",
      version: 6,
      partialize: ({
        copySentImagesToCanvas,
        voiceHintSeenAt,
        syncScreenshotsByAccount,
      }) => ({
        syncScreenshotsByAccount,
        copySentImagesToCanvas,
        voiceHintSeenAt,
      }),
      migrate: (persisted, version) => {
        const old =
          persisted && typeof persisted === "object"
            ? (persisted as Partial<ScreenshotSettings>)
            : {};
        // Legacy global grants have no account attribution and cannot authorize
        // a cloud copy for whoever signs in next.
        const choices =
          version >= 6 ? syncChoices(old.syncScreenshotsByAccount) : {};
        return {
          syncScreenshots: currentChoice(choices),
          syncScreenshotsByAccount: choices,
          copySentImagesToCanvas:
            version >= 3 && old.copySentImagesToCanvas === true,
          voiceHintSeenAt:
            typeof old.voiceHintSeenAt === "number" &&
            Number.isFinite(old.voiceHintSeenAt)
              ? old.voiceHintSeenAt
              : null,
        };
      },
    },
  ),
);

function refreshAccountChoice() {
  useScreenshotSettings.setState({
    syncScreenshots: currentChoice(
      useScreenshotSettings.getState().syncScreenshotsByAccount,
    ),
  });
}
refreshAccountChoice();
const stopAccountSync = subscribeDesktopChatAuthScope(refreshAccountChoice);
const stopPreferenceSync = syncPersistedPreferences({
  name: "screenshots",
  read: () => {
    const {
      copySentImagesToCanvas,
      voiceHintSeenAt,
      syncScreenshotsByAccount,
    } = useScreenshotSettings.getState();
    return {
      copySentImagesToCanvas,
      voiceHintSeenAt,
      syncScreenshotsByAccount,
    };
  },
  subscribe: useScreenshotSettings.subscribe,
  apply: (value) => {
    if (!value || typeof value !== "object") return;
    const incoming = value as Partial<ScreenshotSettings>;
    if (
      typeof incoming.copySentImagesToCanvas !== "boolean" ||
      !(
        incoming.voiceHintSeenAt === null ||
        (typeof incoming.voiceHintSeenAt === "number" &&
          Number.isFinite(incoming.voiceHintSeenAt))
      )
    )
      return;
    const choices = incoming.syncScreenshotsByAccount
      ? syncChoices(incoming.syncScreenshotsByAccount)
      : useScreenshotSettings.getState().syncScreenshotsByAccount;
    useScreenshotSettings.setState({
      syncScreenshotsByAccount: choices,
      syncScreenshots: currentChoice(choices),
      copySentImagesToCanvas: incoming.copySentImagesToCanvas,
      voiceHintSeenAt: incoming.voiceHintSeenAt,
    });
  },
});
if (import.meta.hot)
  import.meta.hot.dispose(() => {
    stopAccountSync();
    stopPreferenceSync();
  });
